import React, { useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../context/LanguageContext';
import { ROUTES } from '../../../../routes/Paths';
import '../style/main-slider.css';
import { initializeMainSlider } from '../script/main-slider';
import { getMainSliderSlides, SlideData } from '../../../../../data/content/MainSliderData';
import { trackPhoneClick } from '../../../../components/email/utils/trackingUtils';

const MainSlider: React.FC = () => {
    const { t } = useLanguage();
    const swiperRef = useRef<HTMLDivElement>(null);
    const swiperInstanceRef = useRef<any>(null);

    // Type assertion for nested translation access
    const translation = t as any;

    // Get slides with translations
    const slides: SlideData[] = useMemo(() => {
        return getMainSliderSlides(translation);
    }, [translation]);

    useEffect(() => {
        if (!swiperRef.current || swiperInstanceRef.current) {
            return;
        }

        // Initialize Swiper using the script function
        initializeMainSlider(swiperRef.current)
            .then((swiperInstance) => {
        if (swiperInstance) {
            swiperInstanceRef.current = swiperInstance;
        }
            })
            .catch(() => {
            });

        // Cleanup
        return () => {
            if (swiperInstanceRef.current) {
                try {
                swiperInstanceRef.current.destroy(true, true);
                swiperInstanceRef.current = null;
                } catch (_) {
                }
            }
        };
    }, []);

    return (
        <section className="main-slider">
            <div 
                ref={swiperRef}
                className="swiper-container thm-swiper__slider"
            >
                <div className="swiper-wrapper">
                    {slides.map((slide, index) => (
                        <div key={index} className="swiper-slide">
                            {/* Video Background */}
                            <video 
                                className="main-slider__video-bg" 
                                muted 
                                loop 
                                playsInline 
                                preload="auto"
                                autoPlay
                            >
                                <source src={slide.videoSrc} type="video/webm" />
                                Your browser does not support the video tag.
                            </video>
                            
                            {/* Fallback Background Image */}
                            <div 
                                className="main-slider__bg"
                                style={{
                                    backgroundImage: `url(${slide.videoSrc})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    width: '100%',
                                    height: '100%',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    zIndex: 0,
                                    opacity: 0,
                                    pointerEvents: 'none'
                                }}
                            />
                            
                            <div className="container">
                                <div className="row">
                                    <div className="col-xl-12">
                                        <div className="main-slider__content">
                                            <h4 className="main-slider__sub-title">
                                                {slide.subtitle}
                                            </h4>
                                            {index === 0 ? (
                                                <h1
                                                    className="main-slider__title"
                                                    dangerouslySetInnerHTML={{ __html: slide.title }}
                                                />
                                            ) : (
                                                <h3
                                                    className="main-slider__title"
                                                    dangerouslySetInnerHTML={{ __html: slide.title }}
                                                />
                                            )}
                                            <p 
                                                className="main-slider__text"
                                                dangerouslySetInnerHTML={{ __html: slide.text }}
                                            />
                                            <div className="main-slider__btn-and-review-box">
                                                <div className="main-slider__btn-box">
                                                    <Link 
                                                        to={ROUTES.BUDGET} 
                                                        className="thm-btn slider-appointment-btn"
                                                    >
                                                        {t.BUTTONS.requestQuote}
                                                        <span><i className="icon-next"></i></span>
                                                    </Link>
                                                    {/* Secondary call button (A3/A6): visible above the fold */}
                                                    <a
                                                        href="tel:+351219010100"
                                                        className="thm-btn main-slider__call-btn"
                                                        onClick={() => trackPhoneClick('hero_homepage')}
                                                    >
                                                        {(t.BUTTONS as any).callUsNow || 'Fale connosco'}
                                                        <span><i className="icon-phone-call"></i></span>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="swiper-pagination" id="main-slider-pagination"></div>
            </div>
        </section>
    );
};

export default MainSlider;

